import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Lightbulb, RefreshCw, Sun, Moon, ChevronUp, ChevronDown, Square, Play, Trash2, Plus, Zap, Cloud, Wifi, WifiOff, Film } from "lucide-react";

/**
 * Licht & Rollos — Eaton xComfort SHC Steuerung.
 * Zeigt Discovery-Aktoren, Szenen-Verwaltung, Auto-Dim pro Vorstellung.
 */

const DEFAULT_SCENES = [
  { name: "Einlass", description: "Warmes Licht 70 %", action_hint: "70" },
  { name: "Filmstart", description: "Sanftes Abdimmen auf 5 %", action_hint: "5" },
  { name: "Pause", description: "Rampe auf 50 %", action_hint: "50" },
  { name: "Filmende", description: "Voll an 100 %", action_hint: "100" },
];

const isDimmer = (a) => (a.type || a.deviceType || "").toString().toLowerCase().includes("dim");
const isBlind = (a) => {
  const t = (a.type || a.deviceType || "").toString().toLowerCase();
  return t.includes("shut") || t.includes("blind") || t.includes("rollo") || t.includes("jal");
};
const inferValue = (a) => {
  if (a.value !== undefined && a.value !== null) return a.value;
  if (a.state !== undefined && a.state !== null) return a.state;
  return "";
};

export default function Licht() {
  const [status, setStatus] = useState(null);
  const [actors, setActors] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("actors"); // actors | scenes
  const [sceneBuilder, setSceneBuilder] = useState(null); // {name, description, actions:[]}

  const loadAll = async () => {
    try {
      const [s, a, sc] = await Promise.all([
        api.get("/xcomfort/status"),
        api.get("/xcomfort/actors/cached"),
        api.get("/xcomfort/scenes"),
      ]);
      setStatus(s.data);
      setActors(a.data.actors || []);
      setScenes(sc.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadAll(); }, []);

  const discover = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/xcomfort/actors");
      setActors(data.actors);
      toast.success(`${data.count} Aktoren gefunden`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "SHC nicht erreichbar");
    } finally { setLoading(false); }
  };

  const cmd = async (zone_id, actor_id, value) => {
    try {
      await api.post("/xcomfort/command", { zone_id, actor_id, value });
      toast.success("OK");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const runScene = async (sc) => {
    try {
      const { data } = await api.post(`/xcomfort/scenes/${sc.id}/run`);
      toast.success(`Szene "${sc.name}" — ${data.executed} Aktionen`);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const deleteScene = async (sc) => {
    if (!window.confirm(`Szene "${sc.name}" löschen?`)) return;
    await api.delete(`/xcomfort/scenes/${sc.id}`);
    loadAll();
  };

  const saveScene = async () => {
    if (!sceneBuilder?.name) { toast.error("Name fehlt"); return; }
    if (!sceneBuilder.actions?.length) { toast.error("Mindestens eine Aktion"); return; }
    try {
      await api.post("/xcomfort/scenes", sceneBuilder);
      toast.success("Szene gespeichert");
      setSceneBuilder(null);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const a of actors) {
      const zone = a.zone || a.zoneName || a.zoneId || "Ohne Zone";
      g[zone] = g[zone] || [];
      g[zone].push(a);
    }
    return g;
  }, [actors]);

  const modePill = status?.mode === "local" ? { icon: Wifi, cls: "text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/10", label: "Lokal · Heimnetz" }
    : status?.mode === "cloud" ? { icon: Cloud, cls: "text-[#EEDC82] border-[#8B7328]/60 bg-[#8B7328]/10", label: "Cloud · Eaton" }
    : { icon: WifiOff, cls: "text-[#A8A398] border-[#2A2A2A] bg-[#0F0F0F]", label: "Nicht verbunden" };
  const ModeIcon = modePill.icon;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="licht-page">
      <div className="flex items-center gap-4 flex-wrap">
        <Lightbulb size={22} className="text-[#D4AF37]" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">Steuerung</div>
          <h1 className="font-heading text-3xl">Licht & Rollos</h1>
        </div>
        <div className={`inline-flex items-center gap-2 border rounded-full px-3 py-1 text-[10px] uppercase tracking-widest ${modePill.cls}`} data-testid="xcomfort-mode">
          <ModeIcon size={11} /> {modePill.label}
        </div>
        <button onClick={discover} disabled={loading} data-testid="xcomfort-discover"
          className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37] text-[#D4AF37] rounded text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10 disabled:opacity-40">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Aktoren scannen
        </button>
      </div>

      {status?.last_error && !actors.length && (
        <div className="card-lux p-4 border-[#7A1921]/40 bg-[#5B1319]/10">
          <div className="text-xs text-[#EE9CA1] font-medium mb-1">⚠️ SHC nicht erreichbar</div>
          <div className="text-[10px] text-[#A8A398] mt-1 font-mono">{status.last_error}</div>
          <div className="text-[10px] text-[#F5F0E6] mt-3 space-y-1.5">
            <div><span className="text-[#D4AF37]">▸ Ursache 1:</span> Preview läuft in der Emergent-Cloud und kann deine lokale IP <span className="font-mono">{status?.local_ip_set ? "192.168.178.27" : "(nicht gesetzt)"}</span> nicht erreichen.</div>
            <div><span className="text-[#D4AF37]">▸ Ursache 2:</span> Eaton-Cloud (`mysmarthome.eaton.com`) blockiert die Anmeldung über einen serverseitigen Container (Cloudflare 302-Loop).</div>
            <div className="text-[#EEDC82] mt-3 pt-2 border-t border-[#2A2A2A]">Lösung:</div>
            <div>1. <b>Deploy in Produktion</b>: die deployte Version kann evtl. anders routen — teste dort erneut</div>
            <div>2. <b>LAN-Bridge</b> (empfohlen): richte <a href="https://tailscale.com" className="text-[#D4AF37] underline" target="_blank" rel="noreferrer">Tailscale</a> oder Cloudflare-Tunnel im Heimnetz ein und trage die neue IP in <span className="font-mono">XCOMFORT_LOCAL_IP</span> ein</div>
            <div>3. <b>Getestet & funktionsfähig</b>: alle Endpoints (Discovery, Steuerung, Szenen, Auto-Dim) sind fertig und laufen sofort, sobald der SHC erreichbar ist</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#1A1A1A]">
        <button onClick={() => setTab("actors")} data-testid="licht-tab-actors"
          className={`px-4 py-2 text-[10px] uppercase tracking-[0.3em] border-b-2 ${tab === "actors" ? "border-[#D4AF37] text-[#EEDC82]" : "border-transparent text-[#A8A398]"}`}>
          Aktoren ({actors.length})
        </button>
        <button onClick={() => setTab("scenes")} data-testid="licht-tab-scenes"
          className={`px-4 py-2 text-[10px] uppercase tracking-[0.3em] border-b-2 ${tab === "scenes" ? "border-[#D4AF37] text-[#EEDC82]" : "border-transparent text-[#A8A398]"}`}>
          Szenen ({scenes.length})
        </button>
      </div>

      {tab === "actors" && (
        <div className="space-y-6">
          {!actors.length && (
            <div className="card-lux p-8 text-center text-[#A8A398]">
              Noch keine Aktoren geladen. Klick auf <span className="text-[#D4AF37]">„Aktoren scannen“</span> um die Geräte vom SHC abzurufen.
            </div>
          )}
          {Object.keys(grouped).sort().map((zone) => (
            <div key={zone} className="card-lux p-5">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-4">{zone}</div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {grouped[zone].map((a) => {
                  const zid = a.zoneId || zone;
                  const aid = a.id || a.deviceId || a.actorId;
                  const val = inferValue(a);
                  return (
                    <div key={aid} className="border border-[#1F1F1F] bg-[#0A0A0A] rounded p-3 space-y-2" data-testid={`actor-${aid}`}>
                      <div className="flex items-baseline gap-2">
                        <div className="font-medium text-sm flex-1 truncate">{a.name || aid}</div>
                        <div className="text-[9px] font-mono text-[#575148]">{a.type || a.deviceType || "—"}</div>
                      </div>
                      <div className="text-[10px] text-[#A8A398]">Status: <span className="font-mono text-[#EEDC82]">{String(val)}</span></div>

                      {isBlind(a) ? (
                        <div className="grid grid-cols-3 gap-1.5">
                          <button onClick={() => cmd(zid, aid, "up")} className="py-1.5 border border-[#2A2A2A] rounded text-xs hover:border-[#D4AF37]" data-testid={`blind-up-${aid}`}><ChevronUp size={14} className="mx-auto" /></button>
                          <button onClick={() => cmd(zid, aid, "stop")} className="py-1.5 border border-[#2A2A2A] rounded text-xs hover:border-[#D4AF37]" data-testid={`blind-stop-${aid}`}><Square size={12} className="mx-auto" /></button>
                          <button onClick={() => cmd(zid, aid, "down")} className="py-1.5 border border-[#2A2A2A] rounded text-xs hover:border-[#D4AF37]" data-testid={`blind-down-${aid}`}><ChevronDown size={14} className="mx-auto" /></button>
                        </div>
                      ) : isDimmer(a) ? (
                        <div className="space-y-2">
                          <div className="flex gap-1.5">
                            <button onClick={() => cmd(zid, aid, "off")} className="flex-1 py-1.5 border border-[#2A2A2A] rounded text-[10px] uppercase tracking-widest hover:border-[#D4AF37]" data-testid={`dim-off-${aid}`}>Aus</button>
                            {[10, 30, 50, 70, 100].map((p) => (
                              <button key={p} onClick={() => cmd(zid, aid, p)}
                                className="flex-1 py-1.5 border border-[#2A2A2A] rounded text-[10px] hover:border-[#D4AF37]" data-testid={`dim-${p}-${aid}`}>
                                {p}%
                              </button>
                            ))}
                          </div>
                          <input type="range" min={0} max={100} defaultValue={Number(val) || 0}
                            onMouseUp={(e) => cmd(zid, aid, Number(e.target.value))}
                            onTouchEnd={(e) => cmd(zid, aid, Number(e.target.value))}
                            className="w-full accent-[#D4AF37]" data-testid={`dim-slider-${aid}`} />
                        </div>
                      ) : (
                        <div className="flex gap-1.5">
                          <button onClick={() => cmd(zid, aid, "on")} className="flex-1 py-1.5 border border-[#D4AF37]/40 text-[#D4AF37] rounded text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10" data-testid={`sw-on-${aid}`}><Sun size={12} className="inline mr-1" />An</button>
                          <button onClick={() => cmd(zid, aid, "off")} className="flex-1 py-1.5 border border-[#2A2A2A] rounded text-[10px] uppercase tracking-widest hover:border-[#7A1921] hover:text-[#EE9CA1]" data-testid={`sw-off-${aid}`}><Moon size={12} className="inline mr-1" />Aus</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "scenes" && (
        <div className="space-y-4">
          {/* Existing scenes */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {scenes.map((sc) => (
              <div key={sc.id} className="card-lux p-4 space-y-2" data-testid={`scene-${sc.id}`}>
                <div className="flex items-start gap-2">
                  <Zap size={14} className="text-[#D4AF37] mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{sc.name}</div>
                    <div className="text-[10px] text-[#A8A398]">{sc.description || `${sc.actions?.length || 0} Aktionen`}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => runScene(sc)} data-testid={`scene-run-${sc.id}`}
                    className="flex-1 py-1.5 bg-[#D4AF37] hover:bg-[#EEDC82] text-black rounded text-[10px] uppercase tracking-widest font-medium inline-flex items-center justify-center gap-1">
                    <Play size={11} /> Starten
                  </button>
                  <button onClick={() => deleteScene(sc)} className="p-1.5 border border-[#2A2A2A] rounded hover:border-[#7A1921] hover:text-[#EE9CA1]" data-testid={`scene-del-${sc.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Vorschläge */}
          {!sceneBuilder && (
            <div className="card-lux p-4">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mb-3">Neue Szene aus Vorlage</div>
              <div className="grid sm:grid-cols-4 gap-2">
                {DEFAULT_SCENES.map((pre) => (
                  <button key={pre.name} onClick={() => setSceneBuilder({ name: pre.name, description: pre.description, actions: [] })}
                    data-testid={`scene-preset-${pre.name}`}
                    className="border border-[#1F1F1F] rounded p-3 text-left hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transitions-safe">
                    <div className="text-xs font-medium">{pre.name}</div>
                    <div className="text-[10px] text-[#575148] mt-1">{pre.description}</div>
                  </button>
                ))}
                <button onClick={() => setSceneBuilder({ name: "", description: "", actions: [] })}
                  data-testid="scene-empty"
                  className="border border-[#D4AF37]/40 rounded p-3 text-left hover:bg-[#D4AF37]/10 transitions-safe">
                  <Plus size={14} className="text-[#D4AF37]" />
                  <div className="text-xs font-medium mt-1">Leere Szene</div>
                </button>
              </div>
            </div>
          )}

          {/* Builder */}
          {sceneBuilder && (
            <SceneBuilder
              scene={sceneBuilder}
              actors={actors}
              onChange={setSceneBuilder}
              onCancel={() => setSceneBuilder(null)}
              onSave={saveScene}
            />
          )}
        </div>
      )}
    </div>
  );
}

function SceneBuilder({ scene, actors, onChange, onCancel, onSave }) {
  const addAction = () => {
    const first = actors[0];
    if (!first) { toast.error("Zuerst Aktoren scannen"); return; }
    onChange({ ...scene, actions: [...scene.actions, {
      zone_id: first.zoneId || first.zone || "",
      actor_id: first.id || first.deviceId || first.actorId,
      value: isBlind(first) ? "up" : (isDimmer(first) ? 50 : "on"),
      delay_ms: 0,
    }] });
  };
  const patchAction = (idx, patch) => {
    const arr = scene.actions.slice();
    arr[idx] = { ...arr[idx], ...patch };
    onChange({ ...scene, actions: arr });
  };
  const removeAction = (idx) => {
    onChange({ ...scene, actions: scene.actions.filter((_, i) => i !== idx) });
  };
  return (
    <div className="card-lux p-5 space-y-4" data-testid="scene-builder">
      <div className="grid sm:grid-cols-2 gap-3">
        <input value={scene.name} onChange={(e) => onChange({ ...scene, name: e.target.value })} placeholder="Name (z.B. Filmstart)"
          className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" data-testid="scene-name-input" />
        <input value={scene.description} onChange={(e) => onChange({ ...scene, description: e.target.value })} placeholder="Beschreibung"
          className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" data-testid="scene-desc-input" />
      </div>
      <div className="space-y-2">
        {scene.actions.map((a, i) => (
          <div key={i} className="grid sm:grid-cols-[1fr_1fr_100px_80px_auto] gap-2 items-center">
            <select value={a.actor_id} onChange={(e) => {
                const act = actors.find((x) => (x.id || x.deviceId || x.actorId) === e.target.value);
                patchAction(i, { actor_id: e.target.value, zone_id: act?.zoneId || act?.zone || a.zone_id });
              }}
              className="bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs">
              {actors.map((x) => <option key={x.id || x.deviceId} value={x.id || x.deviceId || x.actorId}>{x.name || x.id} · {x.type || ""}</option>)}
            </select>
            <input value={String(a.value)} onChange={(e) => patchAction(i, { value: /^\d+$/.test(e.target.value) ? Number(e.target.value) : e.target.value })}
              className="bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs font-mono" placeholder="on/off/0-100/up/down/stop" />
            <input type="number" min={0} max={30000} value={a.delay_ms} onChange={(e) => patchAction(i, { delay_ms: Number(e.target.value) })}
              className="bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1.5 text-xs font-mono" placeholder="Delay ms" />
            <div className="text-[9px] text-[#575148] font-mono truncate">{a.zone_id}</div>
            <button onClick={() => removeAction(i)} className="text-[#EE9CA1] p-1"><Trash2 size={12} /></button>
          </div>
        ))}
        <button onClick={addAction} className="w-full py-2 border border-dashed border-[#2A2A2A] rounded text-[10px] uppercase tracking-widest text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]" data-testid="scene-add-action">
          <Plus size={12} className="inline mr-1" /> Aktion hinzufügen
        </button>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 border border-[#2A2A2A] rounded text-xs uppercase tracking-widest">Abbrechen</button>
        <button onClick={onSave} data-testid="scene-save" className="px-4 py-2 bg-[#D4AF37] hover:bg-[#EEDC82] text-black rounded text-xs uppercase tracking-widest font-medium">
          Speichern
        </button>
      </div>
      <div className="text-[10px] text-[#575148] flex items-start gap-1">
        <Film size={11} className="mt-0.5" />
        Tipp: In der Vorstellungs-Bearbeitung kannst du eine Szene als Auto-Szene beim Filmstart hinterlegen.
      </div>
    </div>
  );
}
